import { Box, IconButton, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'src/components/new/Button';
import { Typography } from 'src/components/new/Typography';
import BkSnackBar from 'src/components/snackbar/BkSnackBar';
import {
  createDocSheet,
  deleteDocSheet,
  getDocSheets,
  getDocumentProcessingRates,
  updateDocProcessingRates,
  updateDocSheet,
} from 'src/features/admin/utils';
import { AppDispatch, RootState } from 'src/store/store';
import PendingIcon from '@mui/icons-material/Pending';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { clearUnsavedDocProcessingRates, updateUnsavedDocProcessingRates } from 'src/features/admin/adminSlice';
import BkLoader from 'src/components/loader/BkLoader';
import Select from 'src/components/new/inputs/Select';
import AddEditPriceSheet from '../modals/AddEditPriceSheet';
import DeleteSheetConfirmation from '../modals/DeleteSheetConfirmation';
import DocProcessingColumn from './components/DocProcessingColumn';
import { DocProcessingRate } from 'src/features/admin/constants';
import { formatPrice, validateInput } from './utils/utils';

interface SnackbarProps {
  type: 'success' | 'error';
  msg: string;
}

const mapSheetsData = (data: any) => {
  const mappedSheets = [
    ...(data ?? []).map((item: any) => ({
      id: item.id.toString(),
      label: item.id === 1 ? 'Default' : item.id === 2 ? 'Chapter 13 Trustee' : item.name,
    })),
    { id: 'addNewSheet', label: 'Add New Sheet' },
  ];
  return mappedSheets;
};

const DocumentProcessingRates: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const docProcessingRates = useSelector((state: RootState) => state.admin.documentProcessingRates);
  const docSheets = useSelector((state: RootState) => state.admin.docSheets);
  const unsavedChanges = useSelector((state: RootState) => state.admin.unsavedDocProcessingRatesChanges);
  const unsavedChangesIds = Object.keys(unsavedChanges).map(Number);

  const [showSnackbarMsg, setShowSnackbarMsg] = useState<SnackbarProps>({
    msg: '',
    type: 'success',
  });
  const [isEditingEnabled, setIsEditingEnabled] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [selectedSheetId, setSelectedSheetId] = useState('1');
  const [isAddSheetModalOpen, setIsAddSheetModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [nameToEdit, setNameToEdit] = useState('');

  const onEditClick = () => {
    setIsEditingEnabled(true);
  };

  const onDiscardChangesClick = () => {
    setIsEditingEnabled(false);
    dispatch(clearUnsavedDocProcessingRates());
    setShowSnackbarMsg({ msg: 'Changes discarded!', type: 'success' });
  };

  useEffect(() => {
    dispatch(getDocSheets()).then(() => {
      dispatch(getDocumentProcessingRates(selectedSheetId));
    });
  }, []);

  useEffect(() => {
    if (selectedSheetId === 'addNewSheet') {
      setIsAddSheetModalOpen(true);
    } else {
      dispatch(getDocumentProcessingRates(selectedSheetId));
      setIsEditingEnabled(false);
      dispatch(clearUnsavedDocProcessingRates());
      setIsAddSheetModalOpen(false);
    }
  }, [selectedSheetId]);

  const onSaveChangesClick = () => {
    if (unsavedChangesIds.length === 0) {
      setIsEditingEnabled(false);
      return;
    }
    if (Object.values(unsavedChanges).some((item) => item.length === 1)) {
      setShowSnackbarMsg({ msg: 'Please complete all fields before saving', type: 'error' });
      return;
    }
    setShowLoader(true);
    dispatch(updateDocProcessingRates(unsavedChanges)).then((response) => {
      if (updateDocProcessingRates.fulfilled.match(response)) {
        setShowSnackbarMsg({ msg: 'Changes saved successfully!', type: 'success' });
        dispatch(getDocumentProcessingRates(selectedSheetId)).then(() => {
          dispatch(clearUnsavedDocProcessingRates());
          setShowLoader(false);
        });
      }
      if (updateDocProcessingRates.rejected.match(response)) {
        setShowSnackbarMsg({ msg: 'Something went wrong!', type: 'error' });
        setShowLoader(false);
      }
    });
    setIsEditingEnabled(false);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
    const isValidInput = validateInput(e.target.value, 2);
    if (!isValidInput) return;
    const formattedPrice = formatPrice(e.target.value, 2);
    dispatch(updateUnsavedDocProcessingRates({ id, value: formattedPrice }));
  };

  const onAddDocSheet = (name: string) => {
    if (nameToEdit) {
      dispatch(updateDocSheet({ id: selectedSheetId, name })).then((result) => {
        if (updateDocSheet.fulfilled.match(result)) {
          dispatch(getDocSheets()).then(() => {
            setShowSnackbarMsg({ type: 'success', msg: 'Sheet name updated successfully!' });
            dispatch(getDocumentProcessingRates(selectedSheetId));
          });
        }
      });
      setNameToEdit('');
      setIsAddSheetModalOpen(false);
    } else {
      dispatch(createDocSheet(name)).then((result) => {
        if (createDocSheet.fulfilled.match(result)) {
          setShowSnackbarMsg({ type: 'success', msg: 'Sheet created successfully!' });
          dispatch(getDocSheets()).then((result) => {
            if (getDocSheets.fulfilled.match(result)) {
              const latestSheetId = result.payload.items[result.payload.items.length - 1].id.toString();
              setSelectedSheetId(latestSheetId);
              dispatch(getDocumentProcessingRates(latestSheetId));
            }
          });
        }
        if (createDocSheet.rejected.match(result)) {
          const errorMessage =
            typeof result.payload === 'string' ? result.payload : 'Something went wrong while creating sheet';
          setShowSnackbarMsg({ type: 'error', msg: errorMessage });
        }
      });
    }
  };

  const onDeleteDocSheet = () => {
    dispatch(deleteDocSheet(selectedSheetId)).then((result) => {
      if (deleteDocSheet.fulfilled.match(result)) {
        setShowSnackbarMsg({ type: 'success', msg: 'Sheet deleted successfully!' });
        setSelectedSheetId('1');
        dispatch(getDocSheets()).then(() => {
          dispatch(getDocumentProcessingRates('1'));
        });
      }
      if (deleteDocSheet.rejected.match(result)) {
        const errorMsg = (result.payload as string) ?? 'Error occured while deleting sheet!';
        setShowSnackbarMsg({ type: 'error', msg: errorMsg });
      }
    });
    setIsDeleteModalOpen(false);
  };

  const onEditButtonClick = () => {
    const sheet = docSheets.find((sheet) => sheet.id === +selectedSheetId);
    setIsAddSheetModalOpen(true);
    setNameToEdit(sheet?.name ?? '');
  };

  const normalizeRatesForUI = (rates: DocProcessingRate[]) => {
    return rates.map((rate) => ({
      id: rate.id,
      name: rate.name,
      value: unsavedChanges[rate.id] ?? `$${rate.price.toFixed(2)}`,
      isDirty: unsavedChanges[rate.id] !== undefined,
    }));
  };

  const onCloseAddEditModal = () => {
    if (selectedSheetId === 'addNewSheet') {
      setSelectedSheetId('1');
    }
    setNameToEdit('');
    setIsAddSheetModalOpen(false);
  };

  return (
    <>
      <BkSnackBar
        open={!!showSnackbarMsg.msg}
        handleCloseSnackbar={() => setShowSnackbarMsg({ type: 'success', msg: '' })}
        snackMessage={showSnackbarMsg.msg}
        severity={showSnackbarMsg.type}
      />
      <BkLoader open={showLoader} loaderTitle="Updating Document Processing Rates..." />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 3 }}>
          <Typography sx={{ fontFamily: 'ABC Arizona Mix' }} fontWeight={300} fontSize={32}>
            Print Pricing
          </Typography>
          {Object.keys(unsavedChanges).length !== 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                backgroundColor: '#ffeedf',
                borderRadius: '999px',
                padding: 1,
              }}
            >
              <PendingIcon sx={{ display: 'inline', color: '#7b3f07' }} />
              <Typography sx={{ color: '#7b3f07', fontWeight: 500 }}>
                {Object.keys(unsavedChanges).length} unsaved changes
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flex: 1.5 }}>
          {isEditingEnabled && (
            <>
              <Button startIcon={<DeleteIcon fontSize="small" />} type="text" onClick={onDiscardChangesClick}>
                Discard Changes
              </Button>
              <Button startIcon={<CheckIcon fontSize="small" />} type="primary" onClick={onSaveChangesClick}>
                Save Changes
              </Button>
            </>
          )}
          {!isEditingEnabled && (
            <Button startIcon={<EditIcon fontSize="small" />} type="primary" onClick={onEditClick}>
              Edit Document Processing
            </Button>
          )}
          <Button startIcon={<FileDownloadOutlinedIcon />} type="outline">
            Export
          </Button>
        </Box>
      </Box>
      <Stack direction="row" gap={2}>
        <Select
          data={mapSheetsData(docSheets)}
          label="Name"
          value={selectedSheetId}
          onChange={(e) => {
            setSelectedSheetId(e.target.value);
          }}
          error={''}
          disabled={false}
          containerSx={{ width: '20%', mb: 2 }}
        />
        {selectedSheetId !== '1' && selectedSheetId !== '2' && selectedSheetId !== 'addNewSheet' && (
          <Box sx={{ mt: 2.8 }}>
            <IconButton onClick={onEditButtonClick}>
              <EditIcon />
            </IconButton>
            <IconButton onClick={() => setIsDeleteModalOpen(true)}>
              <DeleteIcon />
            </IconButton>
          </Box>
        )}
      </Stack>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <DocProcessingColumn
          title="Price Per Page"
          onInputChange={onInputChange}
          data={normalizeRatesForUI(docProcessingRates.per_page)}
          isEditable={isEditingEnabled}
        />
        <DocProcessingColumn
          title="Price Per Address"
          onInputChange={onInputChange}
          data={normalizeRatesForUI(docProcessingRates.per_address)}
          isEditable={isEditingEnabled}
        />
        <DocProcessingColumn
          title="Price Per Order"
          onInputChange={onInputChange}
          data={normalizeRatesForUI(docProcessingRates.per_order)}
          isEditable={isEditingEnabled}
        />
      </Box>
      <AddEditPriceSheet
        open={isAddSheetModalOpen}
        onClose={onCloseAddEditModal}
        onSuccess={onAddDocSheet}
        initialName={nameToEdit}
        title={nameToEdit ? 'Edit Price Sheet' : 'Add Price Sheet'}
      />
      <DeleteSheetConfirmation
        open={isDeleteModalOpen}
        onClose={() => {
          setIsEditingEnabled(false);
          setIsDeleteModalOpen(false);
        }}
        onSuccess={onDeleteDocSheet}
      />
    </>
  );
};

export default DocumentProcessingRates;
